import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, Building2, Users, Loader2, UserCheck, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';

/**
 * StagingCommitModal — confirmation dialog before pushing staging data to the CRM.
 * Shows a summary of what will be created/updated and a per-rep breakdown.
 *
 * v2.16.42 (FR-016, Neleke): also surfaces which contacts already exist
 * in the CRM (and will therefore be skipped or fill-empty enriched
 * depending on the import's duplicate_mode), so the post-commit
 * "99 created of 100" is no longer a surprise.
 */
export default function StagingCommitModal({ open, onClose, commitPreview, onCommit, committing, selectedCount = 0 }) {
    const [showExistingDetail, setShowExistingDetail] = useState(false);
    if (!commitPreview) return null;

    const unassigned = (commitPreview.per_rep || []).find(r => r.user_id === 'unassigned');
    const unassignedCount = unassigned ? (unassigned.contacts + unassigned.companies) : 0;
    const existingContacts = commitPreview.contacts_existing ?? 0;
    const newContacts = commitPreview.contacts_new ?? commitPreview.contacts_to_create ?? 0;
    const existingSample = commitPreview.existing_contacts_sample || [];

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        {selectedCount > 0 ? `Commit ${selectedCount} Selected Record${selectedCount !== 1 ? 's' : ''}` : 'Commit to CRM'}
                    </DialogTitle>
                    <DialogDescription>
                        {selectedCount > 0
                            ? `This will create companies and contacts from your ${selectedCount} selected record${selectedCount !== 1 ? 's' : ''}. Existing companies will be matched and updated.`
                            : 'This will create companies and contacts in the CRM from your staged data. Existing companies will be matched and updated.'
                        }
                    </DialogDescription>
                </DialogHeader>

                {/* Summary cards */}
                <div className="grid grid-cols-2 gap-3 my-4">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                        <div className="flex items-center gap-2 text-emerald-700 text-xs font-medium mb-1">
                            <Building2 className="h-3.5 w-3.5" />
                            New Companies
                        </div>
                        <div className="text-2xl font-semibold text-emerald-900">
                            {commitPreview.new_accounts ?? 0}
                        </div>
                    </div>

                    <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                        <div className="flex items-center gap-2 text-stone-600 text-xs font-medium mb-1">
                            <Building2 className="h-3.5 w-3.5" />
                            Existing Companies
                        </div>
                        <div className="text-2xl font-semibold text-stone-800">
                            {commitPreview.existing_accounts ?? 0}
                        </div>
                        <div className="text-[10px] text-stone-500 mt-0.5">will be updated</div>
                    </div>

                    {/* v2.16.42 FR-016: split contacts tile into New / Existing */}
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <div className="flex items-center gap-2 text-blue-700 text-xs font-medium mb-1">
                            <Users className="h-3.5 w-3.5" />
                            New Contacts
                        </div>
                        <div className="text-2xl font-semibold text-blue-900">
                            {newContacts}
                        </div>
                        <div className="text-[10px] text-blue-700/70 mt-0.5">will be created</div>
                    </div>

                    <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                        <div className="flex items-center gap-2 text-stone-600 text-xs font-medium mb-1">
                            <UserCheck className="h-3.5 w-3.5" />
                            Existing Contacts
                        </div>
                        <div className="text-2xl font-semibold text-stone-800">
                            {existingContacts}
                        </div>
                        <div className="text-[10px] text-stone-500 mt-0.5">
                            already in CRM — will be skipped or enriched
                        </div>
                    </div>
                </div>

                {/* v2.16.42 FR-016: expandable list of existing matches so
                    Neleke can see exactly which records won't go through. */}
                {existingContacts > 0 && (
                    <div className="rounded-lg border border-stone-200 bg-white mb-3">
                        <button
                            type="button"
                            onClick={() => setShowExistingDetail(v => !v)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50 transition-colors"
                        >
                            <span className="flex items-center gap-1.5">
                                <UserCheck className="h-3.5 w-3.5 text-stone-500" />
                                Show {existingContacts} existing contact{existingContacts !== 1 ? 's' : ''}
                                {existingSample.length < existingContacts && (
                                    <span className="text-stone-400">(first {existingSample.length})</span>
                                )}
                            </span>
                            {showExistingDetail
                                ? <ChevronUp className="h-3.5 w-3.5 text-stone-400" />
                                : <ChevronDown className="h-3.5 w-3.5 text-stone-400" />}
                        </button>
                        {showExistingDetail && existingSample.length > 0 && (
                            <div className="border-t border-stone-100 max-h-48 overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-stone-50 sticky top-0">
                                        <tr>
                                            <th className="text-left px-3 py-1.5 font-medium text-stone-500">Contact</th>
                                            <th className="text-left px-3 py-1.5 font-medium text-stone-500">Company</th>
                                            <th className="text-left px-3 py-1.5 font-medium text-stone-500">Matched by</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {existingSample.map((m, idx) => (
                                            <tr key={idx} className="border-b border-stone-100 last:border-0">
                                                <td className="px-3 py-1.5 text-stone-700">
                                                    {m.csv_first_name} {m.csv_last_name}
                                                    {m.csv_email && (
                                                        <span className="text-stone-400 text-[10px] ml-1.5">
                                                            <Mail className="inline h-2.5 w-2.5 mr-0.5" />
                                                            {m.csv_email}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-stone-600">{m.csv_company_name || '—'}</td>
                                                <td className="px-3 py-1.5 text-stone-500 capitalize">{m.matched_by}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                <div className="text-[11px] text-stone-500 mb-3 -mt-1">
                    Total records to commit: <strong className="text-stone-700">{commitPreview.total_records ?? 0}</strong>
                </div>

                {/* Per-rep breakdown — v2.16.42 FR-016: split contacts col into New / Existing */}
                {commitPreview.per_rep && commitPreview.per_rep.length > 0 && (
                    <div className="border border-stone-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 border-b border-stone-200">
                                    <th className="text-left px-3 py-2 text-xs font-medium text-stone-500">Rep</th>
                                    <th className="text-right px-3 py-2 text-xs font-medium text-stone-500">Companies</th>
                                    <th className="text-right px-3 py-2 text-xs font-medium text-stone-500">New</th>
                                    <th className="text-right px-3 py-2 text-xs font-medium text-stone-500">Existing</th>
                                </tr>
                            </thead>
                            <tbody>
                                {commitPreview.per_rep.map((rep, idx) => {
                                    const isUnassigned = rep.user_id === 'unassigned';
                                    const newC = rep.contacts_new ?? rep.contacts;
                                    const existC = rep.contacts_existing ?? 0;
                                    return (
                                        <tr
                                            key={rep.user_id || idx}
                                            className={`border-b border-stone-100 last:border-0 ${isUnassigned ? 'bg-amber-50' : ''}`}
                                        >
                                            <td className="px-3 py-2 flex items-center gap-1.5">
                                                {isUnassigned && (
                                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                                )}
                                                <span className={isUnassigned ? 'text-amber-700 font-medium' : 'text-stone-700'}>
                                                    {rep.name || 'Unassigned'}
                                                </span>
                                            </td>
                                            <td className="text-right px-3 py-2 text-stone-600">{rep.companies}</td>
                                            <td className="text-right px-3 py-2 text-blue-700 font-medium">{newC}</td>
                                            <td className="text-right px-3 py-2 text-stone-500">
                                                {existC > 0 ? existC : <span className="text-stone-300">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Unassigned warning banner */}
                {unassignedCount > 0 && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 mt-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">
                            <span className="font-medium">{unassignedCount} records</span> are unassigned
                            and will be owned by you (the admin).
                        </p>
                    </div>
                )}

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose} disabled={committing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={onCommit}
                        disabled={committing}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                        {committing ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Committing...
                            </>
                        ) : (
                            'Commit to CRM'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
